import React, { useMemo } from 'react';
import clsx from 'clsx';
import { DateRange } from 'features/command-center/components/shared/InlineFilters/components/DateRange';
import moment from 'moment';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { DateFormats } from 'UI/constants/defaults';
import { createCustomStaticRanges } from 'UI/utils';

import { useStyles } from './DateRangeFilter.styled';

/**
 * Resume Search adapter around the shared `DateRange` picker.
 *
 * Bridges the picker (uncontrolled, emits JS `Date` objects) to the RHF filters
 * form. The persisted value is a timezone-free `YYYY-MM-DD` calendar date (not a
 * UTC instant): a saved search created in one timezone and reopened in another
 * must resolve to the same calendar day, and only a date-only string round-trips
 * losslessly through both `defaultRange` reconstruction here and the
 * `createdAt` bounds built in `Filters.utils` — a UTC `toISOString()` would drift
 * the day by ±1 across offsets. The `key` forces a remount whenever the external
 * value changes (saved-search restore / clear-all) so the trigger text re-initializes.
 *
 * @param {Object} props
 * @param {{ startDate: string, endDate: string }|null} props.value `YYYY-MM-DD` range or null
 * @param {(next: { startDate: string, endDate: string }|null) => void} props.onChange
 * @param {boolean} [props.disabled]
 */
export const DateRangeFilter = ({ value, onChange, disabled = false }) => {
  const classes = useStyles();
  const staticRanges = useMemo(() => createCustomStaticRanges(additionalPeriodRanges), []);

  const defaultRange = useMemo(() => {
    if (!value?.startDate || !value?.endDate) {
      return undefined;
    }

    const startDate = moment(value.startDate, DateFormats.QueryShortFormat, true);
    const endDate = moment(value.endDate, DateFormats.QueryShortFormat, true);

    if (!startDate.isValid() || !endDate.isValid()) {
      return undefined;
    }

    return { startDate: startDate.toDate(), endDate: endDate.toDate() };
  }, [value?.startDate, value?.endDate]);

  const remountKey = value ? `${value.startDate}:${value.endDate}` : 'empty-date-range';

  const handlePeriodChange = period => {
    if (!period?.startDate || !period?.endDate) {
      onChange(null);
      return;
    }

    onChange({
      startDate: moment(period.startDate).format(DateFormats.QueryShortFormat),
      endDate: moment(period.endDate).format(DateFormats.QueryShortFormat)
    });
  };

  // The shared `DateRange` exposes no `disabled` prop and its inner TextBox opens
  // the calendar on focus, so CSS `pointer-events: none` alone still lets a keyboard
  // user Tab in and open it during loading. Blur any focus that lands while disabled.
  const handleFocusCapture = disabled
    ? event => {
        event.stopPropagation();
        event.target.blur();
      }
    : undefined;

  return (
    <div
      className={clsx(classes.root, { [classes.disabled]: disabled })}
      aria-disabled={disabled}
      onFocusCapture={handleFocusCapture}
    >
      <DateRange
        key={remountKey}
        DateRangerPickerProps={{ staticRanges }}
        defaultRange={defaultRange}
        disableTimeRange
        isPeriodClearable
        monthsToShow={2}
        onPeriodChange={handlePeriodChange}
      />
    </div>
  );
};
